import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { LoadingState, EmptyState, FilterChip } from '../../components/comercial/shared';
import { useAuth } from '../../context/AuthContext';
import { Calendar, MapPin, Clock, Beaker, Wrench, Settings, AlertTriangle, Eye } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_CONFIG = {
  prueba: { label: 'Prueba gratuita', icon: Beaker, color: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  instalacion: { label: 'Instalación definitiva', icon: Wrench, color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  mantenimiento: { label: 'Mantenimiento', icon: Settings, color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  incidencia: { label: 'Incidencia', icon: AlertTriangle, color: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const FILTERS = [
  { id: 'todos', label: 'Todos' },
  { id: 'prueba', label: 'Pruebas' },
  { id: 'instalacion', label: 'Instalaciones' },
  { id: 'mantenimiento', label: 'Mantenimientos' },
  { id: 'incidencia', label: 'Incidencias' },
];

function formatFecha(fecha) {
  if (!fecha) return 'Sin fecha';
  try {
    const d = new Date(fecha + 'T00:00:00');
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return fecha;
  }
}

export default function AgendaPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eventos, setEventos] = useState([]);
  const [filter, setFilter] = useState('todos');

  const fetchAgenda = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/comercial/agenda`, { withCredentials: true });
      setEventos(response.data.eventos || []);
    } catch (err) {
      console.error('Error fetching agenda:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgenda(); }, [fetchAgenda]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filtered = filter === 'todos' ? eventos : eventos.filter((e) => e.tipo === filter);

  // Agrupar por fecha
  const grouped = filtered.reduce((acc, e) => {
    const key = e.fecha || 'sin-fecha';
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  return (
    <ComercialLayout
      title="Agenda"
      subtitle="Vista de solo lectura de tus citas"
      onLogout={handleLogout}
    >
      <div className="space-y-4" data-testid="agenda-page">
        {/* Aviso solo lectura */}
        <div className="flex items-center gap-3 p-4 rounded-xl bg-sky-500/[0.08] border border-sky-500/20">
          <Eye className="w-5 h-5 text-sky-400 flex-shrink-0" />
          <p className="text-sm text-sky-300/80 font-['Manrope']">
            Vista de consulta. La gestión y edición de citas llegará con el <span className="text-sky-300 font-medium">Calendario Inteligente</span> (próxima fase).
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} testId={`agenda-filter-${f.id}`}>
              {f.label}
            </FilterChip>
          ))}
        </div>

        {loading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="Sin citas programadas"
            description="Las pruebas, instalaciones, mantenimientos e incidencias aparecerán aquí automáticamente."
          />
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([fecha, items]) => (
              <div key={fecha}>
                <h3 className="text-sm text-zinc-400 font-medium mb-3 capitalize font-['Manrope']">
                  {fecha === 'sin-fecha' ? 'Sin fecha asignada' : formatFecha(fecha)}
                </h3>
                <div className="space-y-2">
                  {items.map((e) => {
                    const config = TIPO_CONFIG[e.tipo] || TIPO_CONFIG.prueba;
                    const Icon = config.icon;
                    return (
                      <button
                        key={e.id}
                        onClick={() => e.trial_id && navigate(`/comercial/cliente/${e.trial_id}`)}
                        className={`w-full p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] text-left transition-colors ${e.trial_id ? 'hover:border-white/20 cursor-pointer' : 'cursor-default'}`}
                        data-testid={`agenda-evento-${e.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${config.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${config.color}`}>
                                {config.label}
                              </span>
                              <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-white/5 text-zinc-400 border border-white/10">
                                {e.estado_label}
                              </span>
                            </div>
                            <p className="text-sm text-white mt-1 truncate">{e.cliente}</p>
                            {e.direccion && (
                              <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5 truncate">
                                <MapPin className="w-3 h-3 flex-shrink-0" />{e.direccion}
                              </p>
                            )}
                          </div>
                          {e.hora && (
                            <div className="flex items-center gap-1.5 text-sm text-zinc-300 flex-shrink-0">
                              <Clock className="w-4 h-4 text-zinc-500" />
                              {e.hora}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ComercialLayout>
  );
}
